import { createContext, useState } from "react";

const AuthContext = createContext();

// Helper function to safely parse JSON
const safelyParseJSON = (jsonString, defaultValue) => {
  if (!jsonString) return defaultValue;
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Error parsing JSON:', error);
    return defaultValue;
  }
};

export const AuthContextProvider = (props) => {
  const [email, setEmail] = useState(localStorage.getItem("email"));
  const [name, setName] = useState(localStorage.getItem("name"));
  const [sessionToken, setSessionToken] = useState(localStorage.getItem("sessionToken"));
  const [expirationTime, setExpirationTime] = useState(localStorage.getItem("expirationTime"));
  const [dashboardEnabled, setDashboardEnabled] = useState(localStorage.getItem("dashboardEnabled"));
  const [org, setOrg] = useState(localStorage.getItem("org"));
  const [dashboardVersion, setDashboardVersion] = useState(localStorage.getItem("dashboardVersion"));
  
  // Safely parse authLocations
  const [authLocations, setAuthLocations] = useState(() => {
    const savedLocations = localStorage.getItem("authLocations");
    return safelyParseJSON(savedLocations, null);
  });
  
  // Safely parse authLocationData
  const [authLocationData, setAuthLocationData] = useState(() => {
    const savedData = localStorage.getItem("authLocationData");
    return safelyParseJSON(savedData, null);
  });

  const login = (
    email,
    name,
    sessionToken,
    expirationTime,
    authLocations,
    dashboardEnabled,
    authLocationData,
    org,
    dashboardVersion,
  ) => {
    try {
      setEmail(email);
      setName(name);
      setSessionToken(sessionToken);
      setExpirationTime(expirationTime);
      setAuthLocations(authLocations);
      setDashboardEnabled(dashboardEnabled);
      setAuthLocationData(authLocationData);
      setOrg(org);
      setDashboardVersion(dashboardVersion);
      
      localStorage.setItem("email", email);
      localStorage.setItem("name", name);
      localStorage.setItem("sessionToken", sessionToken);
      localStorage.setItem("expirationTime", expirationTime);
      localStorage.setItem("authLocations", JSON.stringify(authLocations));
      localStorage.setItem("dashboardEnabled", dashboardEnabled);
      localStorage.setItem("authLocationData", JSON.stringify(authLocationData));
      localStorage.setItem("org", org);
      localStorage.setItem("dashboardVersion", dashboardVersion);
    } catch (error) {
      console.error("Error during login:", error);
      // If something goes wrong during login, logout to clean state
      logout();
    }
  };

  const logout = () => {
    setEmail(null);
    setName(null);
    setSessionToken(null);
    setExpirationTime(null);
    setAuthLocations(null);
    setDashboardEnabled(null);
    setAuthLocationData(null);
    setOrg(null);
    setDashboardVersion(null);
    // Full clear insurance
    localStorage.clear();
  };

  const loggedIn = !!email;

  const updateAuthLocations = (newAuthLocations) => {
    try {
      setAuthLocations(newAuthLocations);
      localStorage.setItem("authLocations", JSON.stringify(newAuthLocations));
    } catch (error) {
      console.error("Error updating auth locations:", error);
    }
  };
  
  const updateAuthLocationData = (newAuthLocationData) => {
    try {
      setAuthLocationData(newAuthLocationData);
      localStorage.setItem("authLocationData", JSON.stringify(newAuthLocationData));
    } catch (error) {
      console.error("Error updating auth location data:", error);
    }
  };
  
  const updateDashboardEnabled = (newDashboardEnabled) => {
    try {
      setDashboardEnabled(newDashboardEnabled);
      localStorage.setItem("dashboardEnabled", newDashboardEnabled);
    } catch (error) {
      console.error("Error updating dashboard enabled:", error);
    }
  };

  const contextValue = {
    email: email,
    name: name,
    sessionToken: sessionToken,
    expirationTime: expirationTime,
    authLocations: authLocations,
    dashboardEnabled: dashboardEnabled,
    authLocationData: authLocationData,
    org: org,
    dashboardVersion: dashboardVersion,
    login: login,
    logout: logout,
    updateAuthLocations: updateAuthLocations,
    updateAuthLocationData: updateAuthLocationData,
    updateDashboardEnabled: updateDashboardEnabled,
    loggedIn: loggedIn,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContext;